import "./cleaner.css";
import { useCallback, useEffect, useState } from "react";
import { Banner } from "../components/ui";
import { ApiError } from "../lib/api";
import { signOut } from "../lib/auth";
import {
  currentSubscription,
  disablePush,
  enablePush,
  forgetPushOnSignOut,
  isIos,
  isStandalone,
  type PushAvailability,
  PushPermissionError,
  pushAvailability,
  sendTestNotification,
  syncPushSubscription,
} from "../lib/push";
import { clearSessionCache, useSession } from "../session";
import { InstallHelp, PRIVACY_TEXT } from "./help";
import { usePunchQueue } from "./hooks";

type PushState =
  | { name: "checking" }
  | { name: "unavailable"; availability: Extract<PushAvailability, { ok: false }> | null }
  | { name: "ready"; key: string; on: boolean; blocked: boolean };

const UNAVAILABLE_TEXT: Record<Extract<PushAvailability, { ok: false }>["reason"], string> = {
  no_key: "Notifications haven't been set up for this business yet.",
  ios_not_installed:
    "On iPhone, notifications only work in the app on your Home Screen. Add it to your Home Screen (see below), open it from there, then come back here.",
  unsupported: "This browser can't show notifications. Try Safari on iPhone or Chrome on Android.",
  no_service_worker: "Notifications aren't ready yet. Close the app, open it again, then come back here.",
};

function Notifications() {
  const [state, setState] = useState<PushState>({ name: "checking" });
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ kind: "ok" | "warn" | "danger"; text: string } | null>(null);

  const check = useCallback(async () => {
    try {
      const a = await pushAvailability();
      if (!a.ok) {
        setState({ name: "unavailable", availability: a });
        return;
      }
      const sub = await currentSubscription();
      // Make sure the server sends this phone's notifications to whoever is signed in now.
      if (sub) await syncPushSubscription();
      setState({
        name: "ready",
        key: a.key,
        on: !!sub && Notification.permission === "granted",
        blocked: Notification.permission === "denied",
      });
    } catch {
      setState({ name: "unavailable", availability: null });
    }
  }, []);

  useEffect(() => {
    void check();
  }, [check]);

  async function toggle(on: boolean) {
    if (state.name !== "ready") return;
    setBusy(true);
    setMessage(null);
    try {
      if (on) {
        await enablePush(state.key);
        setMessage({ kind: "ok", text: "Notifications are on for this phone." });
      } else {
        await disablePush();
        setMessage({ kind: "ok", text: "Notifications are off for this phone." });
      }
    } catch (e) {
      setMessage({
        kind: e instanceof PushPermissionError ? "warn" : "danger",
        text:
          e instanceof PushPermissionError
            ? "Notifications weren't allowed. You can allow them in your phone's settings for this app."
            : e instanceof ApiError
              ? e.message
              : "That didn't work. Please try again.",
      });
    } finally {
      setBusy(false);
      await check();
    }
  }

  async function test() {
    setBusy(true);
    setMessage(null);
    try {
      const { sent } = await sendTestNotification();
      setMessage(
        sent > 0
          ? { kind: "ok", text: "Sent! It should arrive in a few seconds." }
          : { kind: "warn", text: "No phones are set up for notifications yet. Turn them on first." },
      );
    } catch (e) {
      setMessage({ kind: "danger", text: e instanceof ApiError ? e.message : "That didn't work." });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card stack">
      <h2>Notifications</h2>
      {state.name === "checking" && <p className="muted">Checking this phone…</p>}
      {state.name === "unavailable" && (
        <p className="muted">
          {state.availability
            ? UNAVAILABLE_TEXT[state.availability.reason]
            : "Couldn't check notifications right now. Please try again later."}
        </p>
      )}
      {state.name === "ready" && (
        <>
          <label className="cl-switch">
            <span>
              <strong>Reminders and updates</strong>
              <span className="small muted cl-block">For example, if you forget to clock out.</span>
            </span>
            <input
              type="checkbox"
              role="switch"
              aria-checked={state.on}
              checked={state.on}
              disabled={busy || (state.blocked && !state.on)}
              onChange={(e) => void toggle(e.target.checked)}
            />
          </label>
          {state.blocked && (
            <Banner kind="warn">
              Notifications are blocked for this app.{" "}
              {isIos()
                ? "Go to Settings › Notifications › NKLEAN Hub and turn on Allow Notifications."
                : "In Chrome, tap ⋮ › Settings › Site settings › Notifications and allow this app."}
            </Banner>
          )}
          {state.on && (
            <button className="btn block" onClick={() => void test()} disabled={busy}>
              Send test notification
            </button>
          )}
        </>
      )}
      {message && <Banner kind={message.kind}>{message.text}</Banner>}
    </section>
  );
}

export function CleanerSettingsPage() {
  const { me } = useSession();
  const { mine: queue } = usePunchQueue();
  const [signingOut, setSigningOut] = useState(false);
  const installed = isStandalone();

  async function doSignOut() {
    if (
      queue.length > 0 &&
      !window.confirm(
        "Some of your clock-ins/outs on this phone haven't been sent yet. They are kept on this phone and will only be sent when you sign back in here as yourself. Sign out anyway?",
      )
    ) {
      return;
    }
    setSigningOut(true);
    // Stop this phone getting your notifications (while the session still exists to tell
    // the server), then sign out.
    await forgetPushOnSignOut();
    try {
      await signOut();
    } catch {
      // Go back to the start screen either way.
    }
    // The last known session (used when there's no signal) must not outlive the sign-out.
    try {
      clearSessionCache();
    } catch {}
    window.location.assign("/");
  }

  return (
    <div className="page-narrow cl-page">
      <h1>Settings</h1>

      <Notifications />

      <section className="card stack">
        <h2>Add to your Home Screen</h2>
        {installed ? (
          <p>You're using the installed app. 👍</p>
        ) : (
          <>
            <p>Put NKLEAN Hub on your Home Screen so it opens like a normal app.</p>
            <InstallHelp />
          </>
        )}
        <p className="small muted">
          If you delete the app from your Home Screen, you'll need to sign in again, and any clock-ins saved
          on the phone without signal will be lost.
        </p>
      </section>

      <section className="card stack">
        <h2>Your privacy</h2>
        <p>{PRIVACY_TEXT}</p>
        <p className="muted small">
          It's used only to check you're at the right site. It isn't sent to payroll, and exact locations are
          deleted after a few months.
        </p>
      </section>

      <section className="card stack">
        <h2>Your account</h2>
        {me && (
          <p>
            Signed in as <strong>{me.user.email}</strong>
          </p>
        )}
        <button className="btn block" onClick={() => void doSignOut()} disabled={signingOut}>
          {signingOut ? "Signing out…" : "Sign out"}
        </button>
      </section>
    </div>
  );
}
